import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../state/hooks';
import { omapApi } from '../../services/omapApi';
import FeaturesSection from './settings/FeaturesSection';
import AutonomySection from './settings/AutonomySection';
import SafetySection from './settings/SafetySection';
import ModelSection from './settings/ModelSection';
import { HelpTrigger } from '../help/HelpPanel';
import './SettingsTab.css';

type SubTab = 'features' | 'autonomy' | 'safety' | 'model';

const DEFAULT_PROFILE = {
  domain_levels: {
    financial: { level: 0, thresholds: {} },
    operational: { level: 0, thresholds: {} },
    customer: { level: 0, thresholds: {} },
    learning_growth: { level: 0, thresholds: {} },
  },
  global_circuit_breaker: {
    max_daily_actions: 50,
    max_daily_financial_impact: 5000,
    max_hourly_actions: 10,
    max_consecutive_failures: 3,
    pause_on_anomaly_severity: 'critical',
    pause_on_multi_domain_anomaly: true,
  },
};

const SettingsTab: React.FC = () => {
  const { activeCompanyId } = useAppSelector((state) => state.companies);
  const companyId = activeCompanyId ? parseInt(activeCompanyId.replace('company_', '')) : null;

  const [activeSubTab, setActiveSubTab] = useState<SubTab>('features');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Support the `?tab=model` deep link (e.g. from the "Add your AI provider
  // key" toast on a 403 llm_key_required response).
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    if (t === 'model') setActiveSubTab('model');
  }, []);

  // Feature flags state
  const [flagStates, setFlagStates] = useState<Record<string, boolean>>({});

  // Shared autonomy profile state (used by Autonomy + Safety)
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    setError(null);

    Promise.all([
      omapApi.getCompanyFlags(companyId).catch(() => ({ flags: {} })),
      omapApi.getAutonomyProfile(companyId).catch(() => ({ profile: null })),
    ])
      .then(([flagsData, profileData]) => {
        setFlagStates(flagsData.flags || {});
        setProfile(profileData.profile || DEFAULT_PROFILE);
      })
      .catch(() => setError('Failed to load settings. Please try again.'))
      .finally(() => setLoading(false));
  }, [companyId]);

  async function saveProfile() {
    if (!companyId || !profile) return;
    await omapApi.updateAutonomyProfile(companyId, profile);
  }

  if (!companyId) {
    return <div className="settings-empty">Select a company to manage settings.</div>;
  }

  if (loading) return <div className="settings-loading">Loading settings...</div>;

  if (error) {
    return (
      <div className="settings-empty">
        <p>{error}</p>
        <button className="settings-btn settings-btn-primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="settings-tab">
      <div className="settings-subtabs">
        <button
          className={`settings-subtab ${activeSubTab === 'features' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('features')}
        >
          Features
        </button>
        <button
          className={`settings-subtab ${activeSubTab === 'autonomy' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('autonomy')}
        >
          Autonomy
        </button>
        <button
          className={`settings-subtab ${activeSubTab === 'safety' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('safety')}
        >
          Safety
        </button>
        <button
          className={`settings-subtab ${activeSubTab === 'model' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('model')}
        >
          AI Model
        </button>
        <HelpTrigger section={`settings-${activeSubTab}`} />
      </div>

      {activeSubTab === 'features' && (
        <FeaturesSection
          companyId={companyId}
          flagStates={flagStates}
          onFlagStatesChange={setFlagStates}
        />
      )}
      {activeSubTab === 'autonomy' && (
        <AutonomySection
          companyId={companyId}
          profile={profile}
          onProfileChange={setProfile}
          onSave={saveProfile}
        />
      )}
      {activeSubTab === 'safety' && (
        <SafetySection
          companyId={companyId}
          profile={profile}
          onProfileChange={setProfile}
          onSave={saveProfile}
        />
      )}
      {activeSubTab === 'model' && <ModelSection />}
    </div>
  );
};

export default SettingsTab;
